import React from "react";
import { ResponsiveBar } from "@nivo/bar";
import { dateToString, dateToMonthYear, dateToShortString } from "dateUtils";

// Shared device palette — chart stacks, pages-list bars, and the pie all
// use these so a device reads as the same color everywhere.
export const DEVICE_COLORS = {
  mobile: "#d3963a", // brand gold
  desktop: "#a09c9c", // grey
  unknown: "#55524e", // faint — docs from before device tracking
};

export const DEVICE_LABELS = {
  mobile: "Mobile",
  desktop: "Desktop",
  unknown: "Unknown",
};

function dayStrToDate(dateStr) {
  const [year, month, day] = dateStr.split("-");
  return new Date(Date.UTC(year, month - 1, day, 12));
}

function monthStrToDate(monthStr) {
  const [year, month] = monthStr.split("-");
  return new Date(Date.UTC(year, month - 1, 15, 12));
}

// "0" → "12 AM", "13" → "1 PM"
function hourLabel(hourStr) {
  const h = Number(hourStr);
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${twelve} ${h < 12 ? "AM" : "PM"}`;
}

function bucketLabel(x, granularity) {
  if (granularity === "hour") {
    return `${hourLabel(x)} – ${hourLabel((Number(x) + 1) % 24)}`;
  }
  if (granularity === "month") return dateToMonthYear(monthStrToDate(x));
  return dateToString(dayStrToDate(x));
}

// Dark tooltip card with a per-device breakdown. Shared by the stacked bar
// chart and the most-visited-pages bars so hovers look identical. `values`
// is any object holding a number per device key.
export function DeviceTooltipCard({ label, keys, values }) {
  return (
    <div
      style={{
        background: "#111115",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: "8px",
        padding: "9px 13px",
        fontSize: "13px",
        color: "#f0ede8",
        pointerEvents: "none",
        boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
        minWidth: 150,
      }}
    >
      <div
        style={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          opacity: 0.5,
          marginBottom: "6px",
        }}
      >
        {label}
      </div>
      {keys.map((key) => (
        <div
          key={key}
          style={{ display: "flex", alignItems: "center", gap: "7px", margin: "2px 0" }}
        >
          <span
            style={{
              display: "inline-block",
              width: 9,
              height: 9,
              borderRadius: 2,
              background: DEVICE_COLORS[key],
              flexShrink: 0,
            }}
          />
          <span style={{ opacity: 0.8 }}>{DEVICE_LABELS[key]}</span>
          <span style={{ fontWeight: 700, marginLeft: "auto", paddingLeft: 10 }}>
            {values[key] || 0}
          </span>
        </div>
      ))}
    </div>
  );
}

// Stacked bar chart of unique visitors over time, split by device. `series`
// is [{ x: "YYYY-MM-DD" | "YYYY-MM" | hour string, mobile, desktop, unknown }].
function VisitorsChart({ series, granularity }) {
  // Only stack the "unknown" layer when there's actually legacy data in it.
  const keys = ["mobile", "desktop"];
  if (series.some((d) => d.unknown > 0)) keys.push("unknown");

  // Thin out axis labels so long ranges stay readable.
  const tickEvery = Math.max(1, Math.ceil(series.length / 15));
  const tickValues = series
    .filter((_, i) => i % tickEvery === 0)
    .map((d) => d.x);

  const formatTick = (value) =>
    granularity === "hour"
      ? hourLabel(value)
      : granularity === "month"
        ? dateToMonthYear(monthStrToDate(value))
        : dateToShortString(dayStrToDate(value));

  return (
    <ResponsiveBar
      data={series}
      keys={keys}
      indexBy="x"
      groupMode="stacked"
      margin={{ top: 20, right: 10, bottom: 45, left: 40 }}
      padding={0.25}
      innerPadding={2}
      colors={({ id }) => DEVICE_COLORS[id]}
      borderRadius={5}
      enableLabel={false}
      tooltip={({ data }) => (
        <DeviceTooltipCard
          label={bucketLabel(data.x, granularity)}
          keys={keys}
          values={data}
        />
      )}
      axisBottom={{
        tickSize: 5,
        tickPadding: 10,
        tickRotation: 15,
        tickValues,
        format: formatTick,
      }}
      axisLeft={{ tickSize: 5, tickPadding: 8 }}
      theme={{
        axis: {
          ticks: {
            line: { stroke: "#f8f4f1c1" },
            text: { fill: "#f8f4f1c1" },
          },
          domain: { line: { stroke: "#f8f4f1c1" } },
        },
        grid: { line: { stroke: "#f8f4f1c1", strokeWidth: 1, opacity: 0.15 } },
      }}
    />
  );
}

export default VisitorsChart;
